import React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import FormInputText from '../../components/form/FormInputText';
import { IntegrationSettings } from '../../models/integrationSettings';
import { IntegrationStatus, IntegrationVendor } from '../../models/enums/integrationEnums';
import { parseIntegrationData } from '../../utils/misc-utils';

type TrueworkVoieFormValues = {
  accessKey: string;
  webhookToken: string;
};

interface EditTrueworkVoieIntegrationSettingsProps {
  onCancel: () => void;
  onSave?: (settings: IntegrationSettings) => void;
  integrationSettings?: IntegrationSettings;
  environment: string;
}

export const EditTrueworkVoieIntegrationSettings = ({
  onCancel,
  onSave,
  integrationSettings,
  environment,
}: EditTrueworkVoieIntegrationSettingsProps): JSX.Element => {
  const isEditMode = !!integrationSettings;

  let accessKey = '';
  let webhookToken = '';
  if (isEditMode && integrationSettings?.integrationData) {
    const data = parseIntegrationData(integrationSettings.integrationData);
    accessKey = data.accessKey || '';
    webhookToken = data.webhookToken || '';
  }

  const validationSchema = yup.object().shape({
    accessKey: yup.string().required('Access Key is required'),
    webhookToken: yup.string().required('Webhook Token is required'),
  });

  const { control, handleSubmit, formState } = useForm<TrueworkVoieFormValues>({
    defaultValues: {
      accessKey: accessKey,
      webhookToken: webhookToken,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const { errors } = formState;

  const onSubmit = (data: TrueworkVoieFormValues) => {
    console.log('Form validated successfully:', data);

    if (onSave) {
      let settings: IntegrationSettings;

      if (isEditMode && integrationSettings) {
        settings = {
          ...integrationSettings,
          vendor: IntegrationVendor.Truework,
          environment: environment,
          integrationData: JSON.stringify({
            accessKey: data.accessKey,
            webhookToken: data.webhookToken,
          }),
        };
      } else {
        settings = {
          name: 'Truework VOIE Integration',
          vendor: IntegrationVendor.Truework,
          status: IntegrationStatus.Active,
          environment: environment,
          integrationData: JSON.stringify({
            accessKey: data.accessKey,
            webhookToken: data.webhookToken,
          }),
        };
      }

      onSave(settings);
    }
  };

  return (
    <div className="surface-section p-4 border-1 surface-border border-round mb-3">
      <h3 className="mt-0 mb-3">{isEditMode ? 'Edit Truework VOIE Settings' : 'Truework VOIE Settings'}</h3>
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="pb-3 p-fluid p-input-filled">
        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="accessKey"
            label="Access Key *"
            placeholder="Enter your Truework Access Key"
            type="password"
          />
        </div>

        <div className="field mb-4">
          <FormInputText
            control={control}
            errors={errors}
            field="webhookToken"
            label="Webhook Token *"
            placeholder="Enter your Truework Webhook Token"
            type="password"
          />
        </div>

        <div className="flex justify-content-end gap-2 pt-4">
          <Button
            type="button"
            label="Cancel"
            className="p-button-outlined"
            style={{ width: 'auto', minWidth: 'auto' }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            label={isEditMode ? 'Update' : 'Save'}
            className="p-button"
            style={{ width: 'auto', minWidth: 'auto' }}
          />
        </div>
      </form>
    </div>
  );
};
